import {
	ArrowLeft,
	Clock,
	Maximize,
	Minimize,
	QrCode,
	Square,
	Users,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
import {
	useAttendance,
	useAttendanceQr,
	useCloseAttendance,
} from '@/api/queries/dds-attendance-queries'
import { useDDSById } from '@/api/queries/dds-queries'
import { BrandedQrCode } from '@/components/commons/branded-qr-code'
import { ConfirmDialog } from '@/components/commons/confirm-dialog'
import { DdsMoodIcon } from '@/components/dds-attendance'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ROUTES } from '@/constants/routes'
import { useNow } from '@/hooks/use-now'
import { useScreenWakeLock } from '@/hooks/use-screen-wake-lock'
import { cn } from '@/lib/utils'
import { DDS_MOODS } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'
import {
	buildCheckInUrl,
	getAttendanceErrorCode,
	getAttendanceErrorKey,
	isDdsOpen,
} from '@/utils/dds-attendance'

function formatCountdown(ms: number) {
	const totalSeconds = Math.max(0, Math.ceil(ms / 1000))
	const minutes = Math.floor(totalSeconds / 60)
	const seconds = totalSeconds % 60
	return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function formatTime(iso: string) {
	return new Date(iso).toLocaleTimeString('pt-BR', {
		hour: '2-digit',
		minute: '2-digit',
	})
}

export const DDSAttendancePage = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { id = '' } = useParams()
	const now = useNow()
	const containerRef = useRef<HTMLDivElement>(null)
	const [isFullscreen, setIsFullscreen] = useState(false)
	const [confirmClose, setConfirmClose] = useState(false)

	const { data: dds } = useDDSById(id)
	const attendance = useAttendance(id, true)
	const isOpen = isDdsOpen(attendance.data?.closesAt, now)
	const qr = useAttendanceQr(id, isOpen)
	const closeMutation = useCloseAttendance(id)

	useScreenWakeLock(isOpen)

	// O DDS pode ter sido encerrado em outra aba: sincroniza o status
	const qrErrorCode = getAttendanceErrorCode(qr.error)
	useEffect(() => {
		if (qrErrorCode === 'DDS_CLOSED') attendance.refetch()
	}, [qrErrorCode, attendance.refetch])

	useEffect(() => {
		const onChange = () => setIsFullscreen(!!document.fullscreenElement)
		document.addEventListener('fullscreenchange', onChange)
		return () => document.removeEventListener('fullscreenchange', onChange)
	}, [])

	const toggleFullscreen = () => {
		if (document.fullscreenElement) {
			document.exitFullscreen().catch(() => {})
		} else {
			containerRef.current?.requestFullscreen().catch(() => {})
		}
	}

	const handleError = (error: unknown) => {
		const key = getAttendanceErrorKey(error)
		toast.error(key ? t(key) : getApiErrorMessage(error))
	}

	const handleClose = async () => {
		try {
			await closeMutation.mutateAsync()
			setConfirmClose(false)
		} catch (error) {
			handleError(error)
		}
	}

	const closesAtMs = attendance.data?.closesAt
		? new Date(attendance.data.closesAt).getTime()
		: 0
	const nextQrInMs = qr.data
		? new Date(qr.data.expiresAt).getTime() - 10_000 - now
		: 0

	if (attendance.isError && !attendance.data) {
		return (
			<div className="flex flex-col items-center gap-4 px-4 py-16 text-center">
				<p className="font-semibold text-kaefer-dark-grey">
					{(() => {
						const key = getAttendanceErrorKey(attendance.error)
						return key ? t(key) : getApiErrorMessage(attendance.error)
					})()}
				</p>
				<Button
					className="w-full sm:w-auto"
					onClick={() => navigate(ROUTES.DDS.INDEX)}
					variant="outline"
				>
					<ArrowLeft className="size-4" />
					{t('ddsAttendance.backToList')}
				</Button>
			</div>
		)
	}

	return (
		<div
			className="flex flex-1 flex-col gap-4 bg-background px-4 py-4 sm:px-6"
			ref={containerRef}
		>
			{/* Cabeçalho */}
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="flex min-w-0 items-start gap-2">
					{!isFullscreen && (
						<Button
							aria-label={t('ddsAttendance.backToDds')}
							onClick={() => navigate(ROUTES.DDS.DETAIL.replace(':id', id))}
							size="icon"
							variant="ghost"
						>
							<ArrowLeft className="size-5" />
						</Button>
					)}
					<div className="min-w-0">
						<p className="font-semibold text-kaefer-medium-grey text-xs uppercase tracking-wider">
							{t('ddsAttendance.title')}
						</p>
						<h1 className="truncate font-bold text-kaefer-dark-grey text-xl sm:text-2xl">
							{attendance.data?.subject ?? dds?.subject ?? ''}
						</h1>
					</div>
				</div>
				<div className="flex items-center gap-2">
					{isOpen ? (
						<Badge className="gap-1.5 bg-kaefer-grass-green text-white text-sm">
							<Clock className="size-3.5" />
							{t('ddsAttendance.closesIn', {
								time: formatCountdown(closesAtMs - now),
							})}
						</Badge>
					) : (
						attendance.data && (
							<Badge variant="secondary" className="text-sm">
								{t('ddsAttendance.closed')}
							</Badge>
						)
					)}
					<Button
						aria-label={t('ddsAttendance.fullscreen')}
						onClick={toggleFullscreen}
						size="icon"
						variant="outline"
					>
						{isFullscreen ? (
							<Minimize className="size-4" />
						) : (
							<Maximize className="size-4" />
						)}
					</Button>
				</div>
			</div>

			<div className="grid flex-1 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
				{/* QR Code */}
				<section className="flex flex-col items-center justify-center gap-4 rounded-xl border bg-white p-4 sm:p-6">
					{isOpen ? (
						<>
							<p className="text-center font-semibold text-kaefer-dark-grey sm:text-lg">
								{t('ddsAttendance.scanInstructions')}
							</p>
							<div className="w-full max-w-[min(62vh,480px)]">
								{qr.data ? (
									<BrandedQrCode
										aria-label={t('ddsAttendance.qrLabel')}
										level="Q"
										marginSize={2}
										size={480}
										style={{ width: '100%', height: 'auto' }}
										value={buildCheckInUrl(qr.data.token)}
									/>
								) : (
									<Skeleton className="aspect-square w-full" />
								)}
							</div>
							{qr.data && (
								<p className="flex items-center gap-1.5 text-muted-foreground text-xs">
									<QrCode className="size-3.5" />
									{t('ddsAttendance.nextQrIn', {
										time: formatCountdown(nextQrInMs),
									})}
								</p>
							)}
							{!isFullscreen && (
								<div className="flex flex-wrap justify-center gap-2">
									<Button
										onClick={() => setConfirmClose(true)}
										size="sm"
										variant="destructive"
									>
										<Square className="size-4" />
										{t('ddsAttendance.close')}
									</Button>
								</div>
							)}
						</>
					) : attendance.data ? (
						<div className="flex flex-col items-center gap-3 py-10 text-center">
							<p className="font-bold text-kaefer-dark-grey text-xl">
								{t('ddsAttendance.closedSummary', {
									count: attendance.data.total,
								})}
							</p>
							<div className="flex flex-wrap justify-center gap-2">
								<Button
									className="w-full sm:w-auto"
									onClick={() => navigate(ROUTES.DDS.DETAIL.replace(':id', id))}
									variant="outline"
								>
									{t('ddsAttendance.backToDds')}
								</Button>
							</div>
						</div>
					) : (
						<Skeleton className="aspect-square w-full max-w-sm" />
					)}
				</section>

				{/* Presentes */}
				<section className="flex min-h-0 flex-col rounded-xl border bg-card">
					<header className="flex items-center justify-between border-b px-4 py-3">
						<h2 className="flex items-center gap-2 font-semibold text-kaefer-dark-grey">
							<Users className="size-4" />
							{t('ddsAttendance.present')}
						</h2>
						<span className="font-bold text-2xl text-kaefer-dark-grey tabular-nums">
							{attendance.data?.total ?? 0}
						</span>
					</header>
					{/* Humorômetro da turma (só presenças com humor, ou seja, pelo QR) */}
					<div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
						<span className="text-muted-foreground text-xs">
							{t('ddsAttendance.moodSummary')}
						</span>
						<div className="flex items-center gap-3">
							{DDS_MOODS.map((mood) => (
								<span
									className="flex items-center gap-1 font-semibold text-kaefer-dark-grey text-sm tabular-nums"
									key={mood}
								>
									<DdsMoodIcon className="size-5" mood={mood} />
									{attendance.data?.moodSummary[mood] ?? 0}
								</span>
							))}
						</div>
					</div>
					<ul className="max-h-[50vh] flex-1 divide-y overflow-y-auto lg:max-h-none">
						{attendance.data?.attendees.map((attendee) => (
							<li
								className="flex items-center justify-between gap-3 px-4 py-2.5"
								key={attendee.id}
							>
								<div className="min-w-0">
									<p className="truncate font-medium text-kaefer-dark-grey text-sm">
										{attendee.personName}
									</p>
									<p className="truncate text-muted-foreground text-xs">
										{attendee.badge}
										{attendee.jobRole ? ` · ${attendee.jobRole}` : ''}
									</p>
								</div>
								<div className="flex shrink-0 items-center gap-3">
									{attendee.mood && (
										<DdsMoodIcon className="size-6" mood={attendee.mood} />
									)}
									<div className="flex flex-col items-end gap-0.5">
										<Badge
											className={cn(
												'text-[10px]',
												attendee.source === 'SELF_CHECKIN' &&
													'border-kaefer-grass-green/40 text-kaefer-grass-green',
											)}
											variant="outline"
										>
											{t(`ddsAttendance.source.${attendee.source}`)}
										</Badge>
										<span className="text-muted-foreground text-[10px] tabular-nums">
											{formatTime(attendee.createdAt)}
										</span>
									</div>
								</div>
							</li>
						))}
						{attendance.data?.total === 0 && (
							<li className="px-4 py-10 text-center text-muted-foreground text-sm">
								{t('ddsAttendance.empty')}
							</li>
						)}
					</ul>
				</section>
			</div>

			<ConfirmDialog
				confirmLabel={t('ddsAttendance.close')}
				destructive
				isPending={closeMutation.isPending}
				onConfirm={handleClose}
				onOpenChange={setConfirmClose}
				open={confirmClose}
				title={t('ddsAttendance.closeConfirmTitle')}
			>
				{t('ddsAttendance.closeConfirmDescription')}
			</ConfirmDialog>
		</div>
	)
}
